import { createSlice, current, type PayloadAction } from '@reduxjs/toolkit';
import type SheetAlignmentEnum from 'components/Sheets/enum/sheetAlignment';
import type { AppRoutesEnum } from 'routes';
import { AreaEnum, NavigationSectionsEnum, SheetEnum } from 'utils/constants';
import { isShallowIdentical } from 'utils/isShallowIdentical';

export type Navigation = {
  activeArea: {
    [AreaEnum.MainNavigation]: {
      current: string;
      previous: string;
    };
    [AreaEnum.Faq]: {
      current: string;
      previous: string;
    };
  };
  pageLoaded: boolean;
  sheets: {
    [sheetName: string]: {
      fullyOpen: boolean;
    };
  };
  activeSheets: string[];
  showExitPrompt: boolean;
  visibleMenuModal: any;
  sheetAlignment?: SheetAlignmentEnum;
  lastNavigation?: AppRoutesEnum;
  keyboardArrivedFromSceneIndex: number | null;
  navigateToScene: boolean;
};

const {
  CuttingDoorsCta,
  DistancePiecesCta,
  CustomizeSkytta,
  OpenGuide,
  OpenDesign,
  ShareDesign,
  MyDesigns,
  FAQ,
  HamburgerMenu,
  WhatsIncluded,
  ProductDetails,
  Help,
  BookAMeeting,
  StartFromScratch,
  HomeDelivery,
  StoreSelector,
  LanguageSelector,
  StorageBehind,
  StorageBehindGallery,
  StorageBehindImported,
  UnusedPanels,
  // CustomizeMenu sheets
  CustomizeMeasurements,
  CustomizeDoorLayouts,
  CustomizeFrames,
  CustomizePanels,
  CustomizePanelPatterns,
  CustomizeChr,
  CustomizeStopbars,
  CustomizeDoorHeights
} = SheetEnum;

export const initialSheets = {
  [CustomizeSkytta]: {
    fullyOpen: false
  },
  [CuttingDoorsCta]: {
    fullyOpen: false
  },
  [DistancePiecesCta]: {
    fullyOpen: false
  },
  [HamburgerMenu]: {
    fullyOpen: false
  },
  [Help]: {
    fullyOpen: false
  },
  [OpenDesign]: {
    fullyOpen: false
  },
  [ShareDesign]: {
    fullyOpen: false
  },
  [FAQ]: {
    fullyOpen: false
  },
  [WhatsIncluded]: {
    fullyOpen: false
  },
  [ProductDetails]: {
    fullyOpen: false
  },
  [OpenGuide]: {
    fullyOpen: false
  },
  [BookAMeeting]: {
    fullyOpen: false
  },
  [StartFromScratch]: {
    fullyOpen: false
  },
  [HomeDelivery]: {
    fullyOpen: false
  },
  [StoreSelector]: {
    fullyOpen: false
  },
  [LanguageSelector]: {
    fullyOpen: false
  },
  [StorageBehind]: {
    fullyOpen: false
  },
  [StorageBehindGallery]: {
    fullyOpen: false
  },
  [StorageBehindImported]: {
    fullyOpen: false
  },
  [UnusedPanels]: {
    fullyOpen: false
  },
  [CustomizeMeasurements]: {
    fullyOpen: false
  },
  [CustomizeDoorLayouts]: {
    fullyOpen: false
  },
  [CustomizeFrames]: {
    fullyOpen: false
  },
  [CustomizePanels]: {
    fullyOpen: false
  },
  [CustomizePanelPatterns]: {
    fullyOpen: false
  },
  [CustomizeChr]: {
    fullyOpen: false
  },
  [CustomizeStopbars]: {
    fullyOpen: false
  },
  [CustomizeDoorHeights]: {
    fullyOpen: false
  },
  [MyDesigns]: {
    fullyOpen: false
  }
};

export const initialActiveAreas: Navigation['activeArea'] = {
  [AreaEnum.MainNavigation]: {
    current: NavigationSectionsEnum.Measurements,
    previous: ''
  },
  [AreaEnum.Faq]: {
    current: '',
    previous: ''
  }
};

const initialState: Navigation = {
  activeArea: initialActiveAreas,
  pageLoaded: false,
  sheets: initialSheets,
  activeSheets: [],
  sheetAlignment: undefined,
  visibleMenuModal: null,
  showExitPrompt: false,
  lastNavigation: undefined,
  keyboardArrivedFromSceneIndex: null,
  navigateToScene: false
};

export const { reducer: navigationReducer, actions: navigationAction } = createSlice({
  name: 'navigation',
  initialState,
  reducers: {
    setAllActiveAreas: (state, { payload: activeArea }: PayloadAction<Navigation['activeArea']>) => {
      if (isShallowIdentical(current(state).activeArea, activeArea)) {
        return;
      }

      return { ...state, activeArea };
    },
    /* Makes sure that the first view is mounted before hiding the loading screen */
    setPageLoaded: (state, { payload: pageLoaded }: PayloadAction<Navigation['pageLoaded']>) => {
      if (isShallowIdentical(current(state).pageLoaded, pageLoaded)) {
        return;
      }

      return { ...state, pageLoaded };
    },
    setSheets: (state, { payload: sheets }: PayloadAction<Navigation['sheets']>) => {
      if (isShallowIdentical(current(state).sheets, sheets)) {
        return;
      }

      return { ...state, sheets };
    },
    /**
     *
     * Active sheets in order.
     * Last sheet is the active one and the parent is the next last one.
     */
    setAllActiveSheets: (state, { payload: activeSheets }: PayloadAction<Navigation['activeSheets']>) => {
      if (isShallowIdentical(current(state).activeSheets, activeSheets)) {
        return;
      }

      return { ...state, activeSheets };
    },
    setShowExitPrompt: (state, { payload: showExitPrompt }: PayloadAction<Navigation['showExitPrompt']>) => {
      if (isShallowIdentical(current(state).showExitPrompt, showExitPrompt)) {
        return;
      }

      return { ...state, showExitPrompt };
    },
    setSheetAlignment: (state, { payload: sheetAlignment }: PayloadAction<Navigation['sheetAlignment']>) => {
      if (isShallowIdentical(current(state).sheetAlignment, sheetAlignment)) {
        return;
      }

      return { ...state, sheetAlignment };
    },
    setVisibleMenuModal: (state, { payload: visibleMenuModal }: PayloadAction<Navigation['visibleMenuModal']>) => {
      if (isShallowIdentical(current(state).visibleMenuModal, visibleMenuModal)) {
        return;
      }

      return { ...state, visibleMenuModal };
    },
    setLastNavigation: (state, { payload: lastNavigation }: PayloadAction<Navigation['lastNavigation']>) => {
      if (isShallowIdentical(current(state).lastNavigation, lastNavigation)) {
        return;
      }

      return { ...state, lastNavigation };
    },
    setKeyboardArrivedFromSceneIndex: (
      state,
      { payload: keyboardArrivedFromSceneIndex }: PayloadAction<Navigation['keyboardArrivedFromSceneIndex']>
    ) => {
      if (isShallowIdentical(current(state).keyboardArrivedFromSceneIndex, keyboardArrivedFromSceneIndex)) {
        return;
      }

      return { ...state, keyboardArrivedFromSceneIndex };
    },
    setNavigateToScene: (state, { payload: navigateToScene }: PayloadAction<Navigation['navigateToScene']>) => {
      if (isShallowIdentical(current(state).navigateToScene, navigateToScene)) {
        return;
      }

      return { ...state, navigateToScene };
    }
  }
});
